// API 타입 정의 — plan §1 / §5 / 10_api_examples.http와 1:1 일치.
// backend 응답 shape이 바뀌면 컴파일이 깨지도록 한다.

// ─────────────────────────────────────────────────────────────
// 권한 모델 (plan §1.1, §3)
// ─────────────────────────────────────────────────────────────

export type Level = 'editor' | 'viewer';

// scope는 게임 prefix(소문자) 또는 리터럴 'all' (§3.5 lower-case 고정)
export type Scope = string;

export type Source = 'request' | 'auto_expand' | 'migration' | 'admin_direct';

export type PlatformRole = 'admin' | 'leader' | 'member' | 'none';

export type RequestStatus = 'pending' | 'approved' | 'rejected';

export type RequestType =
  | 'grant'
  | 'revoke'
  | 'project_create'
  | 'project_edit'
  | 'price_apply'
  | 'api_token'
  | 'role_member'
  | 'stage_grant'
  | 'network_meta_apply'
  | 'reseller_allowed_apply';

export type ScopeType = 'game' | 'all';

export type BqSyncStatus = 'pending' | 'done' | 'failed' | 'skipped' | null;

// ─────────────────────────────────────────────────────────────
// 인프라 모니터
// ─────────────────────────────────────────────────────────────

export type MonitoringStatus = 'ok' | 'warning' | 'critical' | 'unknown';

export interface MonitoringSummary {
  ok: number;
  warning: number;
  critical: number;
  unknown: number;
}

export interface MonitoringService {
  name: string;
  type: 'http' | 'dagster' | 'jenkins';
  display_name: string;
  service_group: string;
  category: string;
  description: string;
  base_url: string;
  http_paths: string[];
  http_expected_statuses: number[];
  dns_hosts: string[];
  public_ips: string[];
  ports: number[];
  ssl_min_days_remaining: number;
  timeout_seconds: number;
  graphql_path?: string;
  locations?: string[];
  required_daemons?: string[];
  daemon_heartbeat_max_age_seconds?: number;
}

export interface MonitoringCheckResult {
  key: string;
  name: string;
  status: MonitoringStatus;
  summary: string;
  target: string;
  checked_at: string;
  latency_ms?: number;
  details?: Record<string, unknown>;
}

export interface MonitoringReport {
  name: string;
  status: MonitoringStatus;
  generated_at: string | null;
  summary: MonitoringSummary;
  results: MonitoringCheckResult[];
  services: MonitoringService[];
  alert_rules: Record<string, { warning: boolean; critical: boolean }>;
  latest_run_exists: boolean;
}

export interface MonitoringPollResponse {
  run_id: number;
  status: MonitoringStatus;
  summary: MonitoringSummary;
  notify: boolean;
  sent: boolean;
  pending_count: number;
  suppressed_count: number;
  recovery_count: number;
  cleaned_run_count: number;
}

// ─────────────────────────────────────────────────────────────
// /api/monitoring/bq-slots — BigQuery 슬롯 모니터링
// ─────────────────────────────────────────────────────────────

export type BqJobState = 'RUNNING' | 'PENDING' | 'DONE';

export interface BqSlotReservation {
  name: string;
  baseline_slots: number;
  autoscale_current_slots: number;
  autoscale_max_slots: number;
}

export interface BqSlotScope {
  project: string;
  region: string;
  job_count: number;
  error: string | null;
  reservations?: BqSlotReservation[];
}

export interface BqSlotUser {
  user_email: string;
  is_service_account: boolean;
  total_slot_ms: number;
  looker_slot_ms: number;
  total_bytes_processed: number;
  running: number;
  pending: number;
  job_count: number;
  projects: string[];
  looker_report_ids: string[];
  avg_slots: number;
}

export interface BqSlotJob {
  project: string;
  region: string;
  job_id: string;
  user_email: string;
  state: BqJobState;
  created_at: string | null;
  duration_ms: number;
  total_slot_ms: number;
  avg_slots: number;
  total_bytes_processed: number;
  cache_hit: boolean;
  reservation_id: string | null;
  requestor: string | null;
  looker_report_id: string | null;
  looker_report_url: string | null;
  query_snippet: string;
}

export type BqLoadLevel = 'idle' | 'busy' | 'overloaded' | 'unknown';

export interface BqSlotSummary {
  running: number;
  pending: number;
  total_jobs: number;
  total_slot_ms: number;
  avg_slots: number;
  looker_jobs: number;
  capacity_max_slots: number;
  capacity_current_slots: number;
  // 실행 중 잡들의 실제 슬롯 소비 합 — 부하 판정 기준.
  in_use_slots: number;
  utilization: number | null;
  load_level: BqLoadLevel;
}

export interface BqSlotReport {
  generated_at: string | null;
  window_minutes: number;
  scopes: BqSlotScope[];
  projects: string[];
  summary: BqSlotSummary;
  summary_by_project: Record<string, BqSlotSummary>;
  // key: "project:region" — 리전별 슬롯 풀 기준 집계.
  summary_by_scope: Record<string, BqSlotSummary>;
  users_by_scope: Record<string, BqSlotUser[]>;
  users: BqSlotUser[];
  users_by_project: Record<string, BqSlotUser[]>;
  jobs: BqSlotJob[];
}

// ─────────────────────────────────────────────────────────────
// /api/meta (§5.1)
// ─────────────────────────────────────────────────────────────

export interface GameSummary {
  prefix: string;
  project_id: string | null; // 5자리 숫자 game ID (utils.apps.project_id)
  gcp_project_id: string | null; // GCP 프로젝트 ID 문자열 (utils.apps.gcp_project_id)
  display_name: string | null;
}

// 메뉴별 세분화 권한 레벨 (access < edit_request < edit_direct)
export type MenuLevel = 'access' | 'edit_request' | 'edit_direct';

// 권한 매트릭스가 다루는 메뉴 키 — 소메뉴 단위 (백엔드 MENU_KEYS와 동일 순서)
export const MENU_KEYS = [
  'projects',
  'members',
  'requests',
  'catalog',
  'bq_assets',
  'costs',
  'costs_users',
  'costs_settings',
  'monitoring',
  'monitoring_bq_slots',
  'api_tokens',
  'api_guide',
  'network_meta',
  'reseller_allowed',
] as const;
export type MenuKey = (typeof MENU_KEYS)[number];

export const MENU_LABELS: Record<MenuKey, string> = {
  projects: '프로젝트 목록',
  members: '멤버별 권한',
  requests: '요청함',
  catalog: '데이터 카탈로그 · 카탈로그',
  bq_assets: '데이터 카탈로그 · BQ 미사용 테이블',
  costs: '비용 · 전체 프로젝트',
  costs_users: '비용 · 유저별 비용',
  costs_settings: '비용 · 비용 설정',
  monitoring: '인프라 · 서비스 모니터링',
  monitoring_bq_slots: '인프라 · 빅쿼리 슬롯 모니터링',
  api_tokens: 'API · API 토큰',
  api_guide: 'API · API 가이드',
  network_meta: '네트워크 관리 · Admon 네트워크',
  reseller_allowed: '네트워크 관리 · 리셀러 허용',
};

// ─────────────────────────────────────────────────────────────
// 외부 API 토큰
// ─────────────────────────────────────────────────────────────

export interface ApiTokenRecord {
  id: number;
  name: string;
  owner_email: string;
  token_hint: string;
  expires_at: string | null; // null = 무기한
  request_id: number | null;
  created_at: string;
  revoked_at: string | null;
  revoked_by: string | null;
  last_used_at: string | null;
  reveal_available: boolean; // 평문 1회 확인 가능 여부
}

export interface ApiTokenListResponse {
  tokens: ApiTokenRecord[];
}

// role_menu_map의 레벨 — 'none'은 명시적 접근 불가.
export type RoleMenuLevel = MenuLevel | 'none';

// 역할(leader/member/커스텀) → 메뉴 레벨 매핑. admin·guest(none)는 고정이라 대상이 아니다.
export interface RoleMenuMapResponse {
  roles: Record<string, Partial<Record<MenuKey, RoleMenuLevel>>>;
  menus: string[];
  role_keys: string[];
}

export interface CurrentUser {
  email: string;
  source: string;
  is_default: boolean;
  platform_role: PlatformRole;
  // 보유 역할 키 전체 — admin(승인자) + leader/member/커스텀. 빈 목록이면 guest.
  roles: string[];
  department: string | null;
  menu_levels: Partial<Record<MenuKey, MenuLevel>>;
}

export interface Meta {
  games: GameSummary[];
  allowed_target_email_domains: string[];
  current_user: CurrentUser;
  can_approve_requests: boolean;
  approval_rule_summary: string | null;
  approval_check_error: string | null;
  // 카탈로그 저장 시 BQ 명세 반영 모드 — 'toggle'이면 반영 토글(기본 off) 노출
  catalog_bq_sync_mode?: 'always' | 'toggle';
}

// ─────────────────────────────────────────────────────────────
// /api/projects (§5.2 ~ §5.4)
// ─────────────────────────────────────────────────────────────

export type AppStage = 'STAGE_1' | 'STAGE_2' | 'STAGE_3' | 'STAGE_4' | 'STAGE_5' | 'UA_OPS';
export type ProjectStage = AppStage | null;

export interface ProjectListItem {
  prefix: string;
  project_id: string | null;       // 5자리 숫자 game ID (utils.apps.project_id)
  gcp_project_id: string | null;   // GCP 프로젝트 ID 문자열 (utils.apps.gcp_project_id)
  display_name: string | null;
  stage: ProjectStage;
  ua_active: string | null;
  os: string[];
  member_count: number;
  updated_at: string | null;
  // backend가 채우는 아이콘 proxy URL. null이면 project_id(5자리)가 없는 게임 — placeholder fallback.
  icon_url: string | null;
}

export interface ProjectListResponse {
  projects: ProjectListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface ProjectCommon {
  display_name: string | null;
  app_match_name: string | null;
  bundle_id: string | null;
  ga_game_id: string | null;
  tapjoy_app_id: string | null;
  adjust_app_token: string | null;
  gcp_project_id: string | null;
  publisher: string | null;
  company: string | null;
}

// utils.apps의 한 게임이 지원하는 플랫폼 한 행. 'aos'/'ios' 고정이 아닌 배열로 노출 — 미래에 'web', 'steam' 자동 지원.
export interface PlatformInfo {
  stage?: AppStage | null;
  code: string;                       // 'aos' | 'ios' | 'web' | 'steam' ... utils.apps.store 원본 (lower-case)
  sub_project_id: string | null;      // utils.apps.project_sub_id — 플랫폼별 sub identifier
  store_id: string | null;
  store_url?: string | null;
  bundle_id: string | null;
  sku: string | null;
  ga_game_id: string | null;          // 플랫폼별 GA 게임 ID (aos/ios가 다름)
  meta_app_id?: string | null;
  tapjoy_app_id: string | null;       // 플랫폼별 Tapjoy 앱 ID
  dt_app_id: string | null;           // 플랫폼별 DT FairBid appId — Management API 조회로 채움
  unity_game_id: string | null;       // 플랫폼별 Unity Game ID
  vungle_app_id: string | null;       // 플랫폼별 Vungle(Liftoff) App ID
  state_code: string | null;
}

export interface Project {
  metadata_source?: string | null;
  prefix: string;
  project_id: string | null;       // 5자리 숫자 game ID (utils.apps.project_id)
  gcp_project_id: string | null;   // GCP 프로젝트 ID 문자열 (utils.apps.gcp_project_id)
  display_name: string | null;
  stage: ProjectStage;
  ua_active: string | null;
  // utils.apps.store code 배열 ('aos','ios',...). frontend OSBadges가 'AOS'/'iOS' 표시로 매핑.
  os: string[];
  common: ProjectCommon;
  platforms: PlatformInfo[];
  member_count: number;
  updated_at: string | null;
  // backend가 채우는 아이콘 proxy URL. null이면 project_id(5자리)가 없는 게임 — placeholder fallback.
  icon_url: string | null;
}

export interface ProjectMember {
  email: string;
  level: Level;
  scope: Scope;
  source: Source;
  via_all: boolean;
  granted_at: string;
  granted_by: string | null;
  request_id: number | null;
}

export interface ProjectMembersResponse {
  prefix: string;
  members: ProjectMember[];
}

// ─────────────────────────────────────────────────────────────
// /api/users (§5.5, §5.6)
// ─────────────────────────────────────────────────────────────

export interface UserSummary {
  email: string;
  platform_role: PlatformRole;
  department: string | null;
  last_login_at: string | null;
  grants_count: number;
  has_all_scope: boolean;
}

export interface UsersResponse {
  users: UserSummary[];
}

export interface AccessGrant {
  scope: Scope;
  level: Level;
  source: Source;
  request_id: number | null;
  granted_at: string;
  granted_by: string | null;
  email?: string;
  // 게임 메타 — get_user_grants가 apps 캐시로 enrich (멤버별 권한 상단 박스용). scope='all'/미발견 시 null.
  display_name?: string | null;
  prefix?: string | null;
  icon_url?: string | null;
  os?: string[];
}

export interface UserGrantsResponse {
  email: string;
  platform_role: PlatformRole;
  department: string | null;
  grants: AccessGrant[];
  effective_prefixes: string[];
  has_all_scope: boolean;
}

// ─────────────────────────────────────────────────────────────
// /api/grants (§5.7 ~ §5.10)
// ─────────────────────────────────────────────────────────────

export interface GrantCreateBody {
  email: string;
  scope: Scope;
  level: Level;
}

export interface GrantPatchBody {
  email: string;
  scope: Scope;
  level: Level;
}

export interface GrantDeleteBody {
  email: string;
  scope: Scope;
}

export interface GrantMutationResponse {
  email: string;
  scope: Scope;
  level: Level;
  source: Source;
  granted_at: string;
  granted_by: string | null;
  request_id: number | null;
  bq_sync_status: BqSyncStatus;
  bq_sync_error: string | null;
  // 권한 변경 후 partners staging/utils_us BQ refresh job ID — frontend가 polling으로 진짜 완료 추적.
  sync_job_id: string | null;
}

export interface GrantDeleteResponse {
  email: string;
  scope: Scope;
  deleted: boolean;
  bq_sync_status: BqSyncStatus;
  bq_sync_error: string | null;
  sync_job_id: string | null;
}

export interface GrantBatchBody {
  email: string;
  grants: Array<{ scope: Scope; level: Level }>;
  revoke_scopes: Scope[];
}

export interface GrantBatchResponse {
  email: string;
  applied_count: number;
  failed_count: number;
  bq_sync_status: BqSyncStatus | 'partial';
  bq_sync_error: string | null;
  sync_job_id: string | null;
  sync_warning: string | null;
}

export interface ExpandAllBody {
  email: string;
}

export interface ExpandAllResponse {
  email: string;
  expanded_prefixes: string[];
  level: Level;
  sync_job_id: string | null;
}

export interface RevokeAllBody {
  email: string;
}

export interface RevokeAllResponse {
  email: string;
  revoked_count: number;
  total: number;
  failed: number;
  bq_sync_status: 'done' | 'partial' | 'skipped';
  sync_job_id: string | null;
}

// ─────────────────────────────────────────────────────────────
// /api/costs/bigquery — 비용 대시보드 (admin)
// ─────────────────────────────────────────────────────────────

export interface CostProjectRow {
  project: string;
  total: number;
  by_month: Record<string, number>;
  // 게임 뷰 전용: total = direct + allocated.
  // direct = 직접 귀속 비용, allocated = 미귀속(일괄) 비용을 일별 활성유저(DAU) 비중으로 배분한 몫.
  direct?: number;
  allocated?: number;
  // 버킷(월/일)별 분해 — "직접 + 배분" 셀 표시용. 값이 0인 버킷은 키 자체가 없다.
  by_month_direct?: Record<string, number>;
  by_month_allocated?: Record<string, number>;
  // 게임 뷰 전용: total 중 GA360 service 몫(direct+allocated의 부분집합). UI에서 노랑 강조.
  ga360?: number;
  by_month_ga360?: Record<string, number>;
  // 게임 뷰 전용: total 중 SC Log(자체 서버로그) service 몫(부분집합). UI에서 청록 강조.
  sc_log?: number;
  by_month_sc_log?: Record<string, number>;
}

// 한 필터(합산 또는 특정 service)의 집계 뷰.
export interface CostView {
  projects: CostProjectRow[];
  totals_by_month: Record<string, number>;
  grand_total: number;
}

// 비용 계산 파라미터 — 리전 단가(USD/TiB) + 적용 환율(USD→KRW). 게임별 비용 추정에 사용.
export interface CostSettings {
  cost_per_tib_tokyo: number;
  cost_per_tib_us: number;
  usd_to_krw: number;
}

export interface BigQueryCostResponse {
  currency: string;
  from_month: string;
  to_month: string;
  months: string[];
  services: string[];
  combined: CostView;
  by_service: Record<string, CostView>;
  generated_at: string;
}

// ─────────────────────────────────────────────────────────────
// /api/catalog/* — 데이터 카탈로그 (admin)
// ─────────────────────────────────────────────────────────────

// 원천 소스 — MMP/네트워크/스토어/Firebase/로그서버 등 + 기타 + ''(미지정)
export type CatalogSource =
  | ''
  | 'appsflyer'
  | 'adjust'
  | 'appsflyer_adjust'
  | 'ad_network'
  | 'store'
  | 'firebase'
  | 'supercent_log'
  | 'game_analytics'
  | 'other';

export interface CatalogProject {
  project_id: string;
  label: string;
  kind: 'analytics_hub' | 'game';
}

export interface CatalogProjectsResponse {
  projects: CatalogProject[];
}

// 프로젝트/데이터셋 설명(사람이 입력한 메타)의 공통 필드. 미입력이면 모두 null.
export interface CatalogMetaFields {
  summary: string | null;      // 목록에 보이는 한 줄 요약
  description: string | null;  // 상세 설명
  updated_by: string | null;
  updated_at: string | null;
}

// GET/PUT /api/catalog/project/meta
export interface CatalogProjectMeta extends CatalogMetaFields {
  project_id: string;
}

// GET/PUT /api/catalog/dataset/meta
export interface CatalogDatasetMeta extends CatalogMetaFields {
  project_id: string;
  dataset_id: string;
}

// GET /api/catalog/datasets/meta — 모든 데이터셋(테이블 0개 포함) + 한 줄 요약만.
export interface CatalogDatasetMetaItem {
  dataset_id: string;
  summary: string | null;
}

export interface CatalogDatasetMetasResponse {
  project_id: string;
  datasets: CatalogDatasetMetaItem[];
}

export interface CatalogProjectMetaBody {
  project_id: string;
  summary: string | null;
  description: string | null;
}

export interface CatalogDatasetMetaBody extends CatalogProjectMetaBody {
  dataset_id: string;
}

export interface CatalogTableItem {
  dataset_id: string;
  table_id: string;
  table_type: string; // 'TABLE' | 'VIEW' | 'MATERIALIZED_VIEW' | ...
  full_name: string;  // project.dataset.table
  tags: string[];     // 저장된 태그(목록 필터용)
}

export interface CatalogTablesResponse {
  project_id: string;
  tables: CatalogTableItem[];
  generated_at: string;
}

export interface CatalogColumn {
  name: string;            // 중첩 RECORD는 dotted path
  type: string;
  mode: string;            // NULLABLE | REQUIRED | REPEATED
  bq_description: string | null; // BQ 스키마에 이미 있는 설명
  description: string | null;    // 사람이 입력한 설명(저장 메타)
  source: CatalogSource | string;
}

// BigQuery 파티셔닝 — 시간(DAY/HOUR/MONTH/YEAR) 또는 정수 범위(INTEGER_RANGE).
export interface CatalogPartitioning {
  field: string | null;    // 파티션 기준 컬럼(수집시간 기준이면 null → _PARTITIONTIME)
  type: string;            // 'DAY'|'HOUR'|'MONTH'|'YEAR'|'INTEGER_RANGE'
  require_filter: boolean;  // 쿼리 시 파티션 필터 필수
  expiration_ms: number | null; // 파티션 만료(ms)
}

export interface CatalogTableDetail {
  project_id: string;
  dataset_id: string;
  table_id: string;
  full_name: string;
  table_type: string;
  bq_description: string | null;
  num_rows: number | null;
  num_bytes: number | null;
  modified_at: string | null;
  description: string | null; // 사람이 입력한 테이블 설명
  tags: string[]; // 테이블 태그(허용 목록)
  columns: CatalogColumn[];
  partitioning: CatalogPartitioning | null; // 파티셔닝(없으면 null)
  clustering_fields: string[]; // 클러스터링 컬럼(순서 = 우선순위)
  bigquery_synced?: boolean; // 직전 저장이 BigQuery 실제 명세에 반영됐는지
  bigquery_sync_error?: string | null; // 반영 실패 사유(저장 자체는 성공)
}

export interface CatalogColumnSaveBody {
  name: string;
  description: string | null;
  source: string;
}

export interface CatalogSaveBody {
  project_id: string;
  dataset_id: string;
  table_id: string;
  description: string | null;
  tags: string[];
  columns: CatalogColumnSaveBody[];
  // BigQuery 실제 테이블 명세 반영 여부 — 미전송(운영)이면 반영, 데브는 토글 값 전송.
  apply_to_bigquery?: boolean;
}

export interface CatalogAISuggestion {
  name: string;
  description: string;
  source: string;
}

export interface CatalogAIResponse {
  provider: string; // 'nm' | 'heuristic'
  table_description: string;
  tags: string[];
  columns: CatalogAISuggestion[];
}

// /api/catalog/lineage — BigQuery Data Lineage 기반 계보 그래프.
export interface CatalogLineageNode {
  id: string;
  label: string; // 'dataset.table'
  full_name: string;
  project: string;
  dataset: string;
  table: string;
  kind: 'current' | 'upstream' | 'downstream';
}

export interface CatalogLineageEdge {
  from: string;
  to: string;
}

export interface CatalogLineageRefresh {
  orchestrator: string;
  process: string;
  schedule: string;
  last_updated: string;
}

export interface CatalogLineageResponse {
  full_name: string;
  provider: string; // 'nm' | 'saved' | 'none'
  nodes: CatalogLineageNode[];
  edges: CatalogLineageEdge[];
  refresh: CatalogLineageRefresh;
  saved_at: string;
}

// /api/catalog/table/history — 명세 변경 이력(리비전 + 이전 대비 diff).
export interface CatalogRevisionChange {
  kind: 'table_description' | 'tags' | 'column' | string;
  column: string | null; // kind='column'일 때 컬럼명
  field: 'description' | 'source' | null; // kind='column'일 때 바뀐 필드
  before: string | null;
  after: string | null;
}

export interface CatalogRevisionItem {
  id: number;
  revision: number;
  changed_by: string;
  changed_at: string;
  changes: CatalogRevisionChange[];
}

export interface CatalogHistoryResponse {
  project_id: string;
  dataset_id: string;
  table_id: string;
  revisions: CatalogRevisionItem[];
}

// /api/catalog/table/history/{id} — 리비전 스냅샷 전체(복원용).
export interface CatalogRevisionColumn {
  name: string;
  description: string | null;
  source: string | null;
}

export interface CatalogRevisionSnapshot {
  id: number;
  project_id: string;
  dataset_id: string;
  table_id: string;
  revision: number;
  changed_by: string;
  changed_at: string;
  description: string | null;
  tags: string[];
  columns: CatalogRevisionColumn[];
}

// ─────────────────────────────────────────────────────────────
// /api/sync-jobs/{id} — partners refresh polling (B방식)
// ─────────────────────────────────────────────────────────────

export type PartnersSyncStatus = 'pending' | 'running' | 'done' | 'failed' | 'partial';

export interface PartnersSyncQueryResult {
  label: string;
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  error: string | null;
}

export interface PartnersSyncJob {
  id: string;
  status: PartnersSyncStatus;
  queued_at: string;
  started_at: string | null;
  finished_at: string | null;
  error_message: string | null;
  query_results: PartnersSyncQueryResult[];
}

// ─────────────────────────────────────────────────────────────
// /api/requests (§5.11 ~ §5.19)
// ─────────────────────────────────────────────────────────────

export interface RequestEvent {
  id: number;
  event_type: string;
  actor_email: string | null;
  note: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface RequestRecord {
  id: number;
  request_type: RequestType;
  status: RequestStatus;
  requester_email: string;
  target_email: string | null;
  level: Level | null;
  scope_type: ScopeType | null;
  game_prefixes: string[];
  prefix: string | null;
  payload: Record<string, unknown>;
  reason: string | null;
  requested_at: string;
  decided_at: string | null;
  decided_by: string | null;
  bq_sync_status: BqSyncStatus;
  bq_sync_error: string | null;
  events: RequestEvent[];
}

export interface RequestListResponse {
  requests: RequestRecord[];
}

export interface GrantRequestBody {
  email: string;
  level: Level;
  scope_type: ScopeType;
  games: string[];
  reason?: string;
}

export interface RevokeRequestBody extends GrantRequestBody {}

// POST /api/requests/stage-grant — level=null 은 해당 Stage 권한 회수.
export interface StageGrantRequestBody {
  email: string;
  changes: { stage: AppStage; level: Level | null }[];
  reason: string;
}

export interface ProjectCreateRequestBody {
  display_name: string;
  payload: Record<string, unknown>;
  reason?: string;
}

export interface ProjectEditRequestBody {
  prefix: string;
  payload: Record<string, unknown>;
  reason?: string;
}

export interface ApproveRejectBody {
  comment?: string;
}

export interface CreateRequestResponse {
  request_id: number;
  status: RequestStatus;
  sync_job_id?: string | null;
}

// ─────────────────────────────────────────────────────────────
// /api/admin/users (§5.20, §5.21)
// ─────────────────────────────────────────────────────────────

export interface AdminUser {
  email: string;
  // 대표 역할(admin > leader > 그 외 역할 member > none) — 기존 화면 게이팅 호환.
  platform_role: PlatformRole;
  // 보유 역할 키 전체 — admin 이면 'admin' 포함. 빈 목록이면 guest.
  roles: string[];
  department: string | null;
  last_login_at: string | null;
  grants_count: number;
  has_all_scope: boolean;
  is_explicit_approver: boolean;
  is_in_approver_group: boolean;
  // Peoply 디렉터리 동기화 값 — 수동 department와 분리된 읽기 전용 표시용.
  peoply_department: string | null;
  peoply_is_leader: boolean;
  // Peoply 원본 스냅샷 — 이름 표시·검색, 그룹/팀 필터용. 미등록이면 null.
  peoply_name: string | null;
  peoply_nickname: string | null;
  peoply_job: string | null;
  peoply_title: string | null;
  peoply_group: string | null;
  peoply_team: string | null;
}

export interface AdminUsersResponse {
  users: AdminUser[];
}

// /api/admin/roles — 역할 정의 (admin·guest 는 고정이라 목록에 없다)
export interface RoleDefinition {
  key: string;
  label: string;
  description: string | null;
  // 기본 제공(leader/member) — 삭제 불가, 매핑이 없으면 코드 기본 메뉴가 열린다.
  is_system: boolean;
  user_count: number;
}

export interface RoleListResponse {
  roles: RoleDefinition[];
}

export interface RoleCreateBody {
  key: string;
  label: string;
  description?: string | null;
}

export interface RoleUpdateBody {
  label?: string;
  description?: string | null;
}

export interface AdminUserPatchBody {
  // 부여할 역할 전체(교체). 'admin'은 승인자 지정, 빈 목록은 guest(보유 BigQuery 권한 회수).
  roles?: string[];
  // null로 명시 설정 가능
  department?: string | null;
}
